import { useState } from "react";
import {
  getBugReports,
  createBugReport,
  updateBugReport,
  getVersion,
} from "../data/store";
import type { BugReport, ItemStatus, ItemPriority } from "../types";

const statusColors: Record<ItemStatus, string> = {
  open: "badge-info",
  "in-progress": "badge-warning",
  completed: "badge-success",
  closed: "badge-danger",
};

const severityColors: Record<string, string> = {
  low: "badge-info",
  medium: "badge-warning",
  high: "badge-danger",
  critical: "badge-danger",
};

export default function BugReports() {
  const [bugs, setBugs] = useState(getBugReports());
  const [showForm, setShowForm] = useState(false);
  const [filter, setFilter] = useState<ItemStatus | "all">("all");
  const [form, setForm] = useState({
    title: "",
    description: "",
    priority: "medium" as ItemPriority,
    severity: "medium" as BugReport["severity"],
    author: "Randy",
    stepsToReproduce: "",
  });

  const filtered = filter === "all" ? bugs : bugs.filter((b) => b.status === filter);

  function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    createBugReport({ ...form, status: "open" });
    setBugs(getBugReports());
    setForm({ title: "", description: "", priority: "medium", severity: "medium", author: "Randy", stepsToReproduce: "" });
    setShowForm(false);
  }

  function handleStatusChange(id: string, status: ItemStatus) {
    updateBugReport(id, { status });
    setBugs(getBugReports());
  }

  return (
    <div className="page">
      <div className="page-header">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <h2>Bug Reports</h2>
            <p>Track bugs and link fixes to specific versions.</p>
          </div>
          <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>
            + Report Bug
          </button>
        </div>
      </div>

      {/* Filter tabs */}
      <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1.5rem" }}>
        {(["all", "open", "in-progress", "completed", "closed"] as const).map((s) => (
          <button
            key={s}
            className={`btn ${filter === s ? "btn-primary" : ""}`}
            style={filter !== s ? { background: "var(--card-bg)", border: "1px solid var(--border)" } : {}}
            onClick={() => setFilter(s)}
          >
            {s === "all" ? "All" : s.charAt(0).toUpperCase() + s.slice(1).replace("-", " ")}
          </button>
        ))}
      </div>

      {/* Create form */}
      {showForm && (
        <div className="card" style={{ marginBottom: "1.5rem" }}>
          <h3 style={{ marginBottom: "1rem" }}>Report a Bug</h3>
          <form onSubmit={handleCreate}>
            <div style={{ marginBottom: "1rem" }}>
              <label style={{ display: "block", fontSize: "0.85rem", marginBottom: "0.25rem" }}>Title</label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                required
                style={{ width: "100%", padding: "0.5rem", borderRadius: "4px", border: "1px solid var(--border)" }}
              />
            </div>
            <div style={{ marginBottom: "1rem" }}>
              <label style={{ display: "block", fontSize: "0.85rem", marginBottom: "0.25rem" }}>Description</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                required
                rows={3}
                style={{ width: "100%", padding: "0.5rem", borderRadius: "4px", border: "1px solid var(--border)", resize: "vertical" }}
              />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginBottom: "1rem" }}>
              <div>
                <label style={{ display: "block", fontSize: "0.85rem", marginBottom: "0.25rem" }}>Priority</label>
                <select
                  value={form.priority}
                  onChange={(e) => setForm({ ...form, priority: e.target.value as ItemPriority })}
                  style={{ width: "100%", padding: "0.5rem", borderRadius: "4px", border: "1px solid var(--border)" }}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
              <div>
                <label style={{ display: "block", fontSize: "0.85rem", marginBottom: "0.25rem" }}>Severity</label>
                <select
                  value={form.severity}
                  onChange={(e) => setForm({ ...form, severity: e.target.value as BugReport["severity"] })}
                  style={{ width: "100%", padding: "0.5rem", borderRadius: "4px", border: "1px solid var(--border)" }}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
            </div>
            <div style={{ marginBottom: "1rem" }}>
              <label style={{ display: "block", fontSize: "0.85rem", marginBottom: "0.25rem" }}>Steps to Reproduce</label>
              <textarea
                value={form.stepsToReproduce}
                onChange={(e) => setForm({ ...form, stepsToReproduce: e.target.value })}
                rows={3}
                placeholder="1. Do this&#10;2. Then this&#10;3. See error"
                style={{ width: "100%", padding: "0.5rem", borderRadius: "4px", border: "1px solid var(--border)", resize: "vertical" }}
              />
            </div>
            <button type="submit" className="btn btn-primary">Submit Bug</button>
            <button type="button" className="btn" style={{ marginLeft: "0.5rem" }} onClick={() => setShowForm(false)}>
              Cancel
            </button>
          </form>
        </div>
      )}

      {/* Bug list */}
      <div className="card">
        <table>
          <thead>
            <tr>
              <th>Title</th>
              <th>Severity</th>
              <th>Priority</th>
              <th>Status</th>
              <th>Fixed In</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((b) => {
              const version = b.fixedInVersion ? getVersion(b.fixedInVersion) : null;
              return (
                <tr key={b.id}>
                  <td>
                    <strong>{b.title}</strong>
                    <div style={{ fontSize: "0.8rem", color: "var(--text-muted)", marginTop: "0.25rem" }}>
                      {b.description.substring(0, 80)}{b.description.length > 80 ? "..." : ""}
                    </div>
                  </td>
                  <td><span className={`badge ${severityColors[b.severity]}`}>{b.severity}</span></td>
                  <td><span className={`badge ${severityColors[b.priority]}`}>{b.priority}</span></td>
                  <td><span className={`badge ${statusColors[b.status]}`}>{b.status}</span></td>
                  <td>
                    {version ? (
                      <span className="badge badge-success">v{version.version}</span>
                    ) : (
                      <span style={{ color: "var(--text-muted)", fontSize: "0.85rem" }}>—</span>
                    )}
                  </td>
                  <td style={{ fontSize: "0.85rem" }}>{new Date(b.createdAt).toLocaleDateString()}</td>
                  <td>
                    <select
                      value={b.status}
                      onChange={(e) => handleStatusChange(b.id, e.target.value as ItemStatus)}
                      style={{ padding: "0.25rem", borderRadius: "4px", border: "1px solid var(--border)", fontSize: "0.8rem" }}
                    >
                      <option value="open">Open</option>
                      <option value="in-progress">In Progress</option>
                      <option value="completed">Completed</option>
                      <option value="closed">Closed</option>
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <p style={{ padding: "1rem", color: "var(--text-muted)", textAlign: "center" }}>
            No bug reports found.
          </p>
        )}
      </div>
    </div>
  );
}
